import React, { Suspense, lazy, useContext, useEffect } from "react";
import { Switch, Route, Redirect, useHistory } from "react-router-dom";
import { useLocation } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";
import { css } from "@emotion/react";

import useGaTracker from "./utils/useGaTracker";

import { EditModeProvider } from "./context/editModeContext/editMode.context";
import { UserContextProvider } from "./context/users/users.context";
import { SalesCoachProvider } from "./context/sales-coach/sales-coach.context";

import { AuthenticationContext } from "./context/authentication/authentication.context";
import { useSidebar } from "./context/sidebar/sidebar.context";

import SideMenu from "./components/side-menu/side-menu.component";
import Popup from "./components/popup/popup.component";

const SignUp = lazy(() => import("./pages/sign-up/sign-up.component"));
const Login = lazy(() => import("./pages/login/login.component"));
const RecoveryPassword = lazy(() =>
    import("./pages/recovery-password/recovery-password.component")
);

const AccountPage = lazy(() =>
    import("./pages/account-page/account-page.component")
);
const ManagmentPage = lazy(() =>
    import("./pages/managment/managment-page.component")
);
const ProfilePage = lazy(() =>
    import("./pages/profile/profile-page.component")
);
const DashboardPage = lazy(() =>
    import("./pages/dashboard/dashboard.component")
);
const AnnouncementsPage = lazy(() =>
    import("./pages/announcements/announcements.page")
);
const CreateAnnouncementsPage = lazy(() =>
    import("./pages/create-announcements/create-announcements.page")
);
const Teams = lazy(() => import("./pages/Teams/teams.page"));
const TeamPage = lazy(() => import("./pages/Team/team.page"));
const SalesCoachPage = lazy(() =>
    import("./pages/sales-coach/sales-coach.page")
);
const MenuCoachPromoPage = lazy(() =>
    import("./pages/whats-new/menu-coach.page")
);
const NotFound = lazy(() => import("./pages/404/404.component"));
// const CoursePage = lazy(() => import("./pages/course/course-page.component"));

const override = css`
    display: block;
    margin: auto;
    border-color: #2e1a47;
`;

const AppWrapper = ({ children }) => {
    return (
        <EditModeProvider>
            <UserContextProvider>
                <SalesCoachProvider>
                    <Popup />
                    {children}
                </SalesCoachProvider>
            </UserContextProvider>
        </EditModeProvider>
    );
};

const App = () => {
    const history = useHistory();
    const location = useLocation();

    const {
        currentUser,
        firebaseSessionUser,
        sessionError,
        retrySession,
        signOutUser,
        isLoading,
        authInitialized,
    } = useContext(
        AuthenticationContext
    );
    const { isCollapsed } = useSidebar();

    // Route protection using Firebase authentication state
    useEffect(() => {
        // Only run route protection after Firebase auth has initialized
        if (!authInitialized) return;

        // If user is not logged in and not on allowed public routes, redirect to login
        if (!currentUser && !firebaseSessionUser && !location.pathname.includes("/recovery-password")) {
            history.push("/login");
        }
    }, [currentUser, firebaseSessionUser, authInitialized, history, location.pathname]);

    useGaTracker();

    const notLogin = authInitialized && !isLoading && !currentUser && !firebaseSessionUser;
    const isLogin = authInitialized && !isLoading && currentUser;
    const shouldShowResetPasswordFlow =
        isLogin && currentUser["operator/status"] === "invited";

    // Show loading while Firebase auth is initializing or user data is loading
    if (!authInitialized || isLoading) {
        return (
            <AppWrapper>
                <div
                    style={{
                        height: "100vh",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        overflow: "hidden",
                    }}
                >
                    <ClipLoader
                        color="#2E1A47"
                        loading={true}
                        css={override}
                        size={150}
                    />
                </div>
            </AppWrapper>
        );
    }

    if (firebaseSessionUser && !currentUser) {
        return (
            <AppWrapper>
                <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
                    <p role="alert" className="text-lg text-gray-700">
                        {sessionError || "We could not load your Operator account."}
                    </p>
                    <div className="flex gap-3">
                        <button className="rounded bg-plumb px-4 py-2 text-white" onClick={retrySession}>
                            Retry
                        </button>
                        <button className="rounded border px-4 py-2" onClick={signOutUser}>
                            Sign out
                        </button>
                    </div>
                </div>
            </AppWrapper>
        );
    }

    if (notLogin) {
        return (
            <AppWrapper>
                <Switch>
                    <Suspense
                        fallback={
                            <div
                                style={{
                                    height: "100vh",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <ClipLoader
                                    color="#2E1A47"
                                    css={override}
                                    size={150}
                                />
                            </div>
                        }
                    >
                        <Route path="/recovery-password" component={RecoveryPassword} />
                        <Route path="/login" component={Login} />
                        <Route path="/sign-up" component={SignUp} />
                        <Route exact path="/" render={() => <Redirect to="/login" />} />
                        <Route
                            exact
                            path="/log-in"
                            render={() => <Redirect to="/login" />}
                        />
                    </Suspense>
                </Switch>
            </AppWrapper>
        );
    }

    if (shouldShowResetPasswordFlow) {
        return (
            <AppWrapper>
                <Suspense
                    fallback={
                        <div
                            style={{
                                height: "100vh",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <ClipLoader
                                color="#2E1A47"
                                css={override}
                                size={150}
                            />
                        </div>
                    }
                >
                    <Switch>
                        <Route path="/sign-up" component={SignUp} />
                        <Route exact path="*" render={() => <Redirect to="/sign-up" />} />
                    </Switch>
                </Suspense>
            </AppWrapper>
        );
    }

    return (
        <AppWrapper>
            <div className="flex flex-row bg-alabaster h-screen overflow-hidden">
                <SideMenu />
                <div
                    className={`w-full lg:flex flex-col items-center transition-all duration-300 ease-in-out ${
                        location.pathname.includes("/announcements/all") ||
                        location.pathname.includes("/announcements/saved")
                            ? "overflow-hidden"
                            : location.pathname.includes("/management")
                                ? "overflow-y-auto h-screen"
                                : "overflow-auto"
                    } lg:ml-0`}
                    style={{
                        ...(location.pathname.includes("/management") && {
                            overflowY: 'auto',
                            maxHeight: '100vh',
                            WebkitOverflowScrolling: 'touch'
                        })
                    }}
                >
                    <Suspense
                        fallback={
                            <div
                                style={{
                                    height: "100vh",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <ClipLoader
                                    color="#2E1A47"
                                    css={override}
                                    size={150}
                                />
                            </div>
                        }
                    >
                        <Switch>
                            <Route exact path="/dashboard" component={DashboardPage} />
                            <Route
                                exact
                                path="/announcement/create"
                                component={CreateAnnouncementsPage}
                            />
                            <Route
                                path="/announcements/:type"
                                component={AnnouncementsPage}
                            />
                            <Route
                                exact
                                path="/announcements/"
                                component={AnnouncementsPage}
                            />

                            <Route path="/sales-coach" component={SalesCoachPage} />
                            <Route exact path="/management/staff" component={ManagmentPage} />
                            <Route exact path="/management/teams" component={Teams} />
                            <Route
                                exact
                                path="/management/team/:teamId"
                                component={TeamPage}
                            />
                            <Route path="/account" component={AccountPage} />
                            <Route
                                exact
                                path="/whats-new/menu-coach"
                                component={MenuCoachPromoPage}
                            />
                            {/* <Route path="/course" component={CoursePage} /> */}
                            <Route path="/management/:userId" component={ProfilePage} />
                            <Route
                                exact
                                path="/"
                                render={() => <Redirect to="/dashboard" />}
                            />
                            <Route
                                exact
                                path="/management"
                                render={() => <Redirect to="/management/staff" />}
                            />
                            <Route
                                exact
                                path="/login"
                                render={() => <Redirect to="/dashboard" />}
                            />
                            <Route
                                exact
                                path="/log-in"
                                render={() => <Redirect to="/dashboard" />}
                            />
                            <Route
                                path="/sign-up"
                                render={() => <Redirect to="/dashboard" />}
                            />
                            <Route component={NotFound} />
                        </Switch>
                    </Suspense>
                </div>
            </div>
        </AppWrapper>
    );
};

export default App;
